import React, { useEffect, useRef, useState } from "react";
import { Heading } from "@dip/helsecore/src/components/shared/Heading/Heading";
import { Button } from "@hdir/ui/button";
import Loading from "/react4xp/shared/Cards/Loading/Loading";
import ListSearchResultCardSelector from "/react4xp/shared/ListSearch/ListSearchResultCardSelector";
import HighlightedWrapper from "/react4xp/shared/Cards/HighlightedWrapper/HighlightedWrapper";
import { faArrowDown, faArrowUp } from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIconWrapper } from "@dip/helsecore/src/components/shared/Wrappers/FontAwesomeIconWrapper";

import type { ListObj } from "../../../lib/modules/listSearchUtils/index.d";
import type { ListProps } from "./type.d";

function ListError({ error }: { error: string }) {
  return (
    <p className="p-xl bg-surface-feedback-warning border-2 border-feedback-warning border-dashed p-3 rounded-m">
      {error}
    </p>
  );
}

function ListEmpty() {
  return (
    <p className="p-xl bg-surface-feedback-warning border-2 border-feedback-warning border-dashed p-3 rounded-m">
      Ingen treff. Sjekk om du har valgt innholdstype(r), riktig sortering, riktig antall eller evt. tags.
      Dette vises ikke på live site.
    </p>
  );
}

function ListSkeleton({ count }: { count: number }) {
  return Array.from({ length: count ?? 3 }).map((_, i) => (
    // eslint-disable-next-line react/no-array-index-key
    <Loading key={i} />
  ));
}

export function List(props: ListProps): React.JSX.Element | null {
  const {
    cardBaseId,
    highlightedElements,
    intro,
    isEditMode,
    isPreviewMode,
    isLiveMode,
    layoutHasTitle,
    localizedForList,
    paginationSize,
    initialFetchCount,
    dontFetch,
    queryObject,
    serviceUrl,
    title
  } = props;

  const [listElements, setListElements] = useState([]);
  const [start, setStart] = useState<number>(0);
  const [count, setCount] = useState<number>(initialFetchCount);
  const [isInitialPageLoad, setIsInitialPageLoad] = useState(true);
  const [displayFetchMoreButton, setDisplayFetchMoreButton] = useState(false);
  const [displayShowLessButton, setDisplayShowLessButton] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const shouldFocusRef = useRef(false);
  const prevCountRef = useRef(0);

  /** After "load more", focuses the first newly added card for keyboard users. */
  useEffect(() => {
    if (!shouldFocusRef.current) return;

    const currentList = listElements;

    const oldIndex = prevCountRef.current;
    if (currentList?.length > oldIndex) {
      document.querySelector<HTMLElement>(`[data-item-id="${cardBaseId}-${oldIndex}"]`)?.focus();
    }

    shouldFocusRef.current = false;
  }, [listElements, cardBaseId]);

  useEffect(() => {
    // Return early if missing params or if only highlightedElements should be displayed,  eg. do not fetch
    if (!queryObject?.contentTypes || dontFetch) {
      setListElements([]);
      setIsInitialPageLoad(false);
      return;
    }
    const fetchData = async() => {
      try {
        const params = new URLSearchParams();

        // Handle pagination
        params.set("start", String(start));
        params.set("count", String(count));

        if (queryObject?.query) {
          params.set("query", String(queryObject.query));
        }

        if (queryObject?.sort) {
          params.set("sort", String(queryObject.sort));
        }

        if (queryObject?.contentTypes) {
          params.set("contentTypes", queryObject.contentTypes.join(","));
        }

        if (queryObject?.filters) {
          params.set("filterObject", JSON.stringify(queryObject.filters));
        }

        const serviceUrlWithParams = `${serviceUrl}?${params.toString()}`;
        const response = await fetch(serviceUrlWithParams);

        if (!response.ok) {
          throw new Error(`HTTP error: ${response.status}`);
        }

        const data = await response.json();
        const fetchedList = data.list ?? [];
        const listLength = fetchedList.length;
        // Append to the current list
        const nextList = start === 0 ? fetchedList : [...listElements, ...fetchedList];
        // There are more elements to be fetch
        const hasMore = start + listLength < data.totalCount;

        setIsInitialPageLoad(false);
        setListElements(nextList);
        setDisplayFetchMoreButton(hasMore);
        setDisplayShowLessButton(!hasMore && nextList.length > initialFetchCount);
      } catch (err) {
        setIsInitialPageLoad(false);
        setDisplayFetchMoreButton(true);
        setDisplayShowLessButton(false);
        setError(err instanceof Error ? err.message : "Ukjent feil");
        console.log(err);
      }
    };

    fetchData();
  }, [start, count]);

  const fetchMore = () => {
    shouldFocusRef.current = true;
    prevCountRef.current = listElements.length;
    setCount(paginationSize);
    // Handle difference between inititalFetchCount and paginationSize. For first fetch more (prev === 0), use initialFetchCount as start
    setStart((prev) => prev + (prev === 0 ? initialFetchCount : paginationSize));
  };

  const showLess = () => {
    shouldFocusRef.current = true;
    prevCountRef.current = listElements.length;
    // Reset to initial state
    setCount(initialFetchCount);
    setStart(0);
  };

  const renderList = () => {
    // Render list elements if present
    if (listElements?.length) {
      return (
        <div className="flex flex-col gap-md">
          {listElements.map((element: ListObj, index) => (
            <div key={element.id} data-item-id={`${cardBaseId}-${index}`} tabIndex={-1}>
              <ListSearchResultCardSelector {...element} localizedForListSearch={localizedForList} />
            </div>
          ))}
        </div>
      );
    }

    // Display skeleton card first load. Is also displayed in edit mode the indicate how many cards are loaded.
    if (isInitialPageLoad) return <ListSkeleton count={initialFetchCount} />;
    // If fetched list is empty, display message to editor in preview mode.
    if (isPreviewMode && !(initialFetchCount === 0) && listElements.length <= 0) return <ListEmpty />;
    // If error, display when not on live site. Errors are also logged in console.
    if ((isEditMode || isPreviewMode) && error) return <ListError error={error} />;

    return null;
  };

  // On live page: Only display the part if either or both lists has content
  const hasListContent = (highlightedElements?.length ?? 0) > 0 || (listElements?.length ?? 0) > 0;
  if (isLiveMode && !hasListContent) return null;

  return (
    <div className="py-design-component-mobile tablet:py-design-component-tablet desktop:py-design-component-desktop">
      { title && <Heading className="mb-md" headingLevel={layoutHasTitle ? "h3" : "h2"}>{title}</Heading> }
      { intro && (<p className="my-md max-w-text-width">{intro}</p>)}
      { (highlightedElements && highlightedElements?.length > 0) && (
        <div className="flex flex-col gap-md mb-md">
          { highlightedElements.map((highlightedElement) => (
            <HighlightedWrapper text="Fremhevet" display={!!(highlightedElement.title || highlightedElement.link)}>
              <ListSearchResultCardSelector
                {...highlightedElement}
              />
            </HighlightedWrapper>
          ))}
        </div>
      )}
      { renderList() }
      {(displayFetchMoreButton || displayShowLessButton) && (
        <div className="flex justify-start mt-md">
          {displayFetchMoreButton && (
            <Button variant="secondary" onClick={() => fetchMore()}>
              <FontAwesomeIconWrapper icon={faArrowDown} />
              { localizedForList.showMoreBtn ?? "Vis flere" }
            </Button>
          )}
          {displayShowLessButton && (
            <Button variant="secondary" onClick={() => showLess()}>
              <FontAwesomeIconWrapper icon={faArrowUp} />
              { localizedForList.showLessBtn ?? "Vis færre" }
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

export default function(props: Readonly<ListProps>) {
  return <List {...props} />;
}
